import React, { useState } from "react";
import { useAuth } from "../store/AuthContext";

export const LoginView: React.FC = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const res = await login(email, password, rememberMe);
      if (!res.success) {
        setError(res.error || "Authentication failed. Please verify credentials.");
      }
    } catch {
      setError("An unexpected error occurred during sign in. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-backdrop-glow" />
      
      <div className="login-card-wrapper">
        <div className="login-card">
          {/* Header & Brand */}
          <div className="login-header">
            <div className="login-brand">
              <img
                src="/logo.png"
                alt="Revival Softech"
                className="login-logo-img"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="login-brand-text">
                <h1>
                  Revival <span>Softech</span>
                </h1>
                <div className="login-hub-badge">
                  <i className="fa-solid fa-shield-halved" /> BILLING & FINANCE HUB
                </div>
              </div>
            </div>

            <h2>Secure Portal Login</h2>
            <p>
              Enter your agency email and password to access invoices, quotes, client records, and financial statements.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="login-error-alert" role="alert">
              <i className="fa-solid fa-triangle-exclamation" />
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="login-email">
                <span>Agency Email Address</span>
              </label>
              <div className="login-input-wrap">
                <i className="fa-solid fa-envelope input-icon" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@revivalsoftech.com"
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="login-field">
              <div className="login-label-row">
                <label htmlFor="login-password">
                  <span>Account Password</span>
                </label>
              </div>
              <div className="login-input-wrap">
                <i className="fa-solid fa-lock input-icon" />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                </button>
              </div>
            </div>

            <div className="login-options-row">
              <label className="login-checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me on this device</span>
              </label>
            </div>

            <button
              type="submit"
              className="login-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" />
                  <span>Verifying Credentials...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-arrow-right-to-bracket" />
                  <span>Sign In to Billing Hub</span>
                </>
              )}
            </button>
          </form>

          {/* Security Guarantee Footer */}
          <div className="login-footer-badges">
            <div className="badge-item">
              <i className="fa-solid fa-lock" />
              <span>256-Bit SSL Encrypted</span>
            </div>
            <div className="badge-divider">•</div>
            <div className="badge-item">
              <i className="fa-solid fa-cloud" />
              <span>Cloudflare Edge Secured</span>
            </div>
          </div>
        </div>

        <div className="login-copyright">
          © {new Date().getFullYear()} Revival Softech. All rights reserved.
        </div>
      </div>
    </div>
  );
};
